import { userBasicInfoPublicPages } from 'modules/graphqlQueries';
import {
  Heading,
  SubHeading,
  Title,
  Wrapper,
} from 'styles/terms-and-conditions.styles';

import Layout from '../components/layout/Layout';
import { useLoadData } from '../modules/useLoadData';
import { UserType } from '../types/UserTypes';

const getQuery = (userId) => `{
  user(_id: "${userId}") {
    ${userBasicInfoPublicPages}
  }
}`;

const TermsAndConditions = (): JSX.Element => {
  const {
    isLoading,
    response: {
      data: { user },
    },
  } = useLoadData(getQuery);

  return (
    <Layout user={user as UserType} isLoading={isLoading}>
      <Wrapper>
        <Title>GENERAL TERMS OF USE</Title>
        <Heading id="platform">
          <a href="terms-and-conditions#platform">Platform</a>
        </Heading>

        <p>
          <strong>bid2board PC</strong>, a private limited company organized and
          existing under the laws of Greece, whose registered office is at 2
          Afentouli street, Piraeus, 18536, Greece (“bid2board”) is the sole and
          exclusive owner of an online platform that contains information
          (including, without limitation, data, databases, standards,
          photographs, web logs), web tools, search engines and software (the
          <strong>“Platform”</strong>), fully administered by bid2board.
        </p>

        <p>
          Access to the Platform is conditional upon (a) your registration and
          consent to the present Terms of Use (the{' '}
          <strong>“Terms of Use”</strong>) and the Privacy Policy, and (b)
          creation and or approval of your account by bid2board.
        </p>

        <p>
          Your agreement with bid2board includes the present Terms of Use and
          the Privacy Policy (together the <strong>“Agreement”</strong>). Unless
          otherwise provided herein, defined terms shall have the meaning
          ascribed to them in the Privacy Policy. Before you use the Platform,
          you should read the Terms, and print or save a local copy for your
          records.
        </p>

        <p>
          By registering or signing-in to the Platform, you declare that you are
          authorized to bind yourself and the business or company that you are
          affiliated with (if applicable) to use the Platform. If the Platform
          is being used on behalf of a business or company, such business or
          company shall be responsible for said use as well as for all decisions
          regarding said use, such as the selection of the subscription package
          and the termination of the account.
        </p>

        <p>
          bid2board shall have the right to grant to third parties a license to
          access and use the Platform or any of the material contained therein
          or resulting therefrom at any time.
        </p>

        <Heading id="consent-of-the-terms-of-use">
          <a href="terms-and-conditions#consent-of-the-terms-of-use">
            Consent of the terms of use
          </a>
        </Heading>
        <p>
          You acknowledge that, following your registration and upon creation of
          your account by bid2board you have entered into a legally binding
          agreement with bid2board under the Terms of Use and declared that you
          have the power and authority to do so.
        </p>

        <p>
          You declare that you have read and agree to be bound by the Terms of
          Use. bid2board reserves the right to modify or revise the Terms of Use
          at any time, unilaterally and without notice. The revised Terms of Use
          become effective on the date they are published on the website hosting
          the Platform, which is accessible via{' '}
          <strong>https://bid2board.com</strong> (the
          <strong>“Website”</strong>) and/or the Platform. You are advised to
          periodically check the Terms of Use for any such modifications or
          revisions. Your continued use of the Platform after such changes have
          been posted as provided above constitutes your binding acceptance of
          such changes.
        </p>

        <p>
          If a modification is unacceptable to you, you may terminate this
          agreement by ceasing use of the Platform by providing a relevant prior
          written notice to bid2board.
        </p>

        <Heading id="registration">
          <a href="terms-and-conditions#registration">Registration</a>
        </Heading>
        <p>
          For the purposes of registering and creating an account, you shall
          provide bid2board with registration information that will be
          requested, all of which you warrant and represent to be accurate,
          truthful and will be updated, if any of such information changes. You
          may only register if you are 18 years old and above.
        </p>

        <p>
          You shall be solely responsible for maintaining the confidentiality of
          your username and password.
        </p>

        <p>bid2board reserves the right to deny creation of your account.</p>

        <p>
          You are solely responsible for all use, authorized or unauthorized, of
          the Platform under your username and password. You must notify
          bid2board immediately of any unauthorized use of the Platform.
        </p>

        <p>
          You may not, directly or indirectly, in whole or in part, by operation
          of law or otherwise, assign, transfer or delegate any of your rights
          or obligations under this Agreement without the prior written consent
          of bid2board. Any attempted assignment, transfer or delegation without
          bid2board’s prior written consent will be void and unenforceable.
        </p>

        <Heading id="use-of-platform">
          <a href="terms-and-conditions#use-of-platform">
            Use of the Platform / Restrictions on use
          </a>
        </Heading>
        <ol>
          <li>
            bid2board grants to you a limited, restricted, non-exclusive,
            non-transferable and revocable license to access and use the
            Platform, the material contained therein and resulting therefrom for
            your internal business purposes and/or for your personal use subject
            to the provisions of the Terms of Use. You accept usage limits to
            the Platform and that bid2board may restrict access to it, as set
            forth under the Terms of Use. You may not use the Platform or any of
            the material contained therein or resulting therefrom for resale or
            other transfer or disposition to, or use by, or for the benefit of,
            any other person or entity. You may not use or dispose of the
            Platform or any of the material contained therein or resulting
            therefrom in any manner that could compete with the business of
            bid2board.
          </li>

          <li>
            Except to the extent required by law, or as expressly provided
            herein, you may not copy, take screenshots, (re)distribute,
            (re)publish, transfer, (re)sell, (sub)/(re)license, lease, give,
            store, permanently retain, recompile, decompile, disassemble,
            reverse engineer, disseminate, assign (whether directly or
            indirectly, by operation of law or otherwise), transmit, scan,
            publish on a network, display, or otherwise reproduce, disclose,
            modify, translate into any language or make available to others, or
            create derivative works from, the Platform or any of the material
            contained therein or resulting therefrom.
          </li>

          <li>
            You may not download and/or extract any data or other material
            contained in the Platform or resulting therefrom, either manually or
            automatically, including through the use of web scraping software or
            any technique of screen scraping, web scraping or report mining.
          </li>

          <li>
            You may not remove any copyright notice, trademark and/or other
            proprietary notices of bid2board and/or any third-party providers
            contained on or in the Platform or any of the material contained
            therein or resulting therefrom. You shall take any and all actions
            that may reasonably be required by bid2board to protect the
            proprietary rights as owned by bid2board and/or third-party
            providers.
          </li>

          <li>
            You may not make or retain copies of the Platform or of any of the
            material contained therein or resulting therefrom, in any form,
            except as expressly provided herein.
          </li>

          <li>
            You acknowledge that the Platform, the material contained therein
            and any material resulting therefrom, are developed entirely at
            private expense, comprise valued proprietary and commercial
            information of bid2board and/or third-party providers and are the
            property of bid2board and/or of third-party providers. You
            acknowledge that the license granted herein does not confer any
            ownership rights whatsoever in the Platform or any of the material
            contained therein or resulting therefrom. You must secure the
            Platform, the material contained therein and the material resulting
            therefrom to prevent access by unauthorized individuals or local
            area network users. You shall take all reasonable steps to ensure
            that no unauthorized persons shall have access to the Platform or
            any of the material contained therein or resulting therefrom.
          </li>

          <li>
            You may not use the Platform, or any of the material contained
            therein or resulting therefrom, for any illegal purpose, for the
            facilitation of the violation of any law or regulation, or in any
            manner inconsistent with the Agreement.
          </li>

          <li>
            To the extent applicable under your subscription package, you may
            submit Business Information (as defined below), under the terms
            provided for in the Terms of Use.
          </li>

          <li>
            You acknowledge that any Business Information submitted or any
            exchange of communication between the users of the Platform are also
            disclosed to and monitored by bid2board.
          </li>

          <li>
            You represent and warrant: that you own or otherwise control all of
            the rights to Business Information that you submit; to the extent
            that you submit Business Information, that you have the power and
            authority to submit Business Information for the purposes of the
            Agreement; that Business Information is truthful and accurate; that
            use by bid2board of Business Information for purposes of the
            Agreement does not violate any agreement. You may not submit
            Business Information that: (i) are unlawful, harmful, threatening,
            abusive, harassing, tortious, defamatory, vulgar, obscene, libelous,
            hateful or racially, ethnically or otherwise objectionable; (ii) you
            do not have the right to make available (such as inside information,
            proprietary and confidential information acquired or disclosed as
            part of employment relationships or under non-disclosure
            agreements); (iii) infringe upon any patent, trademark, trade
            secret, copyright or other proprietary rights of any party; (iv)
            contain software viruses or any other computer code, files or
            programs, designed to interrupt, destroy or limit the functionality
            of any computer software or hardware, or telecommunications
            equipment; (v) are promotional in nature, or constitute junk mail,
            spam, chain letters, pyramid schemes or the like. Failure by you to
            comply with this clause might result in termination of the
            Agreement, in accordance with clause 10 herein, and/or claims for
            damages.
          </li>
        </ol>

        <Heading id="fees-and-terms-of-payment">
          <a href="terms-and-conditions#fees-and-terms-of-payment">
            Fees and terms of payment
          </a>
        </Heading>
        <ol>
          <li>
            The Platform and the full range of services and functions are
            provided based on a Fee Policy depending on your business needs and
            preferences as set below:
          </li>

          <SubHeading>For Service Suppliers</SubHeading>
          <ul>
            <li>
              Each service supplier will be burdened with a yearly based fee,
              applicable as soon as the specific supplier receives the
              confirmation for their first order through bid2board platform.
            </li>

            <li>
              After the service supplier’s first order, all additional orders
              received via the platform will be charged with an additional fee
              per order.
            </li>

            <li>
              The supplier will have the option to buy in advance service
              bundles (<strong>“Service Supplier Packages”</strong>) of 10, 20,
              50, 100 or 200 orders at a reduced fee rate.
            </li>
          </ul>
          <SubHeading>For Ship Owners / Managers</SubHeading>
          <ul>
            <li>
              Each Ship Owner / Manager will be burdened with a yearly based fee
              per ship, applicable as soon as they order their first service for
              the specific ship on the platform.
            </li>
            <li>
              Ship Owner’s / Manager’s orders applied via the platform will be
              charged with an additional fee per order.
            </li>
            <li>
              The Ship Owner / Manager will have the option to buy in advance
              service bundles (<strong>“Owner /Manager Packages”</strong>) of
              10, 20, 50, 100 or 200 orders at a reduced fee rate.
            </li>
          </ul>
          <li>
            Depending on your business bid2board will activate your profile
            when:
          </li>
          <ul>
            <li>
              verification of the input data of the profile has been completed
              and the user logs into his/her account (Service Suppliers).
            </li>
            <li>
              the company profile is created and credentials have been provided
              to log in.
            </li>
          </ul>
          <li>
            bid2board issue and provide you with an invoice in respect of the
            fee payable for the use of the Platform and its services. The
            invoice will concern the fixed fee that you have paid for your
            subscription package upon such subscription.
          </li>
          <li>
            bid2board reserves the right to modify, terminate or otherwise amend
            the subscription packages or the fees or the terms of payment. You
            will be notified of any amendments to the subscription packages or
            the fees 15 days before any such amendment becomes effective.
          </li>
          <li>
            All payments which you are liable to make against the use of the
            Platform should be made without deduction or withholding for or on
            account of any tax payable under any law of any pertinent
            jurisdiction.
          </li>
          <li>
            If bid2board has not received payment within 30 (thirty) days after
            the due date, bid2board may, without liability to you, disable your
            password, account and access to all or part of the Service and shall
            be under no obligation to provide any or all of the Service while
            the invoice(s) concerned remain unpaid.
          </li>
          <li>All amounts and fees stated or referred to in these terms:</li>
          <ul>
            <li>shall be payable in euro;</li>
            <li>are non-cancellable and non-refundable;</li>
            <li>
              are exclusive of value added tax, which shall be added to
              bid2board’s invoice(s) at the appropriate rate.
            </li>
          </ul>
        </ol>
        <Heading id="confidentiality">
          <a href="terms-and-conditions#confidentiality">Confidentiality</a>
        </Heading>
        <p>
          Where either you or bid2board (the{' '}
          <strong>&#34;Discloser&#34;</strong>) provides the other party to the
          Agreement (the <strong>&#34;Recipient&#34;</strong>) with Confidential
          Information, such information shall be held in strict confidence and
          shall not be disclosed or used for any purpose unless (a) the prior
          written consent of the Discloser is provided, or (b) required by any
          applicable law or pursuant to the order or demand of a competent
          court, administrative authority or recognized stock exchange, provided
          in such case that the Recipient shall promptly notify the Discloser of
          such requirement before proceeding with any such disclosure and shall
          disclose only that portion of the Confidential Information as is
          legally required to disclose, or (c) as otherwise expressly provided
          in the Agreement. The Recipient shall exercise the same degree of care
          it uses to protect its own confidential or proprietary information but
          no less than a reasonable standard of care.
        </p>
        <p>
          For the purpose of this clause,{' '}
          <strong>&#34;Confidential Information&#34;</strong>
          means: (a) Any business, technical, technological, operational,
          financial, economic, commercial and legal information, including,
          without limitation, any information relating to products, services,
          prices, analytics, technologies, business process management, business
          transactions, business relationships, customers, personnel (b)
          business information provided by you for the use of the Platform
          and/or its services and/or for the purposes of the provision by
          bid2board of broader and more detailed outputs, information and
          results available only to you, unless otherwise provided herein, such
          as information about transactions, vessel name, vessel size, vessel
          position, delivery place, vessel status (
          <strong>“Business Information”</strong>);(c) any other information of
          Discloser that is specifically designated as confidential or
          proprietary; and(d) any information that, by its nature, Recipient
          knows or should know is confidential or proprietary.
        </p>
        <p>
          Confidential Information shall not include: (a) information that is or
          becomes public knowledge other than by a breach of this clause; (b)
          information that is obtained from a third party without breach of an
          obligation of confidentiality; (c) information that can be shown to
          have been independently developed by means other than through access
          to the Confidential Information; (d) if the Platform is being used on
          behalf of a business or company, the name of that business or company,
          its domain name and its logo as user of the Platform; such information
          may be disclosed by bid2board for promotional and/or marketing
          purposes.
        </p>
        <p>
          Each time you submit Business Information, you grant to bid2board the
          limited, irrevocable, non-transferable right of use of said Business
          Information, including, without limitation, the right to read,
          process, analyse, integrate Business Information, combine them or fuse
          them with other data, subject to the limitations of this clause.
          bid2board warrants will not use Business Information that you submit
          and that will be available only to you, unless you otherwise agree or
          unless otherwise provided herein.
        </p>
        <p>Business Information may only be disclosed:</p>
        <ul>
          <li>
            By bid2board to comply with any applicable law, including laws
            outside your country of residence, and to respond to requests from
            courts, public and/or judicial authorities, which may include such
            authorities outside your country of residence, provided that
            bid2board shall provide you with prompt written notice thereof and
            shall disclose only such Business Information as it is required to
            disclose.
          </li>
          <li>
            By bid2board to allow third-party providers to assist bid2board in
            providing you with the services of the Platform, as long as such
            third-party providers have signed a non-disclosure agreement
            providing for a similar standard of protection for Business
            Information as provided for herein.
          </li>
          <li>
            By you on pages, features or any services of the Platform to which
            you are able to post information and materials, provided that a
            relevant notice is displayed regarding the disclosure of said
            information and materials to other users. Please note that any
            information you post or disclose through these pages, features or
            services may be made available to other users as well.
          </li>
          <li>
            By you to anyone with whom you communicate through the Platform.
          </li>
          <li>
            By bid2board to be made available to all users, subject to your
            consent.
          </li>
        </ul>
        <p>
          You shall not provide any Confidential Information about others unless
          you are authorized to do so. By submitting Confidential Information
          about others, you represent and warrant that you are authorized to do
          so and that you have received authorization from the person about whom
          you provide information and that that person has consented to have all
          such information used, disclosed and transferred in accordance with
          this clause.
        </p>
        <p>
          You acknowledge and agree that the employees of bid2board may gain
          access to your Confidential Information. bid2board shall limit such
          access on a need-to-know basis. bid2board shall take all reasonable
          steps to ensure that such employees are informed of the confidential
          nature of the Confidential Information and comply with the provisions
          of this clause.
        </p>
        <p>
          bid2board have put in place reasonable technical, organizational and
          other measures to protect your Confidential Information from
          unauthorized use and/or disclosure. Such measures also apply to
          entities directly or indirectly controlling, controlled by or under
          common control with bid2board.
        </p>
        <Heading id="disclaimer">
          <a href="terms-and-conditions#disclaimer">Disclaimer</a>
        </Heading>
        <p>
          The Platform, the material contained therein and the material
          resulting therefrom is provided by bid2board{' '}
          <strong>&#34;as is&#34;</strong> and{' '}
          <strong>&#34;as available&#34;</strong>. bid2board may add, remove or
          change functionalities or features. bid2board gives no warranties of
          any kind concerning the Platform or any of the material contained
          therein or resulting therefrom, to the fullest extent permissible by
          law. In particular, bid2board expressly disclaims all warranties,
          whether express or implied, of availability, accuracy, completeness,
          currency, reliability, non-infringement of third parties’ rights,
          merchantability or fitness for a particular purpose of any material
          contained in, displayed on, resulting from or otherwise available from
          the Platform, including Business Information and/or third parties’
          information and/or data, if applicable, and expressly disclaims (to
          the maximum extent permitted by any applicable law) any and all
          liability for any damage or loss resulting from use of or reliance on
          the Platform or any of the material contained therein or resulting
          therefrom.
        </p>
        <p>
          Although bid2board takes all reasonable measures to ensure the
          security of its systems, it does not warrant that the services and
          products provided in or through the Platform and/or the material
          contained therein or resulting therefrom will be uninterrupted,
          error-free or free from defects in design or engineering, that defects
          will be corrected or that the online services or the servers that
          render the functions, services and materials available are free of
          viruses or other harmful components. bid2board assumes no
          responsibility for any infection by virus or other harmful material
          that might infect your computer equipment, computer programs, data or
          other proprietary material due to use of the Platform or any of the
          material contained therein or resulting therefrom, and shall not be
          liable for any related costs incurred, including without limitation
          the servicing or replacement of equipment or data. You must take all
          precautions in this respect.
        </p>
        <p>
          Notwithstanding anything to the contrary in the Agreement, in no event
          whatsoever shall bid2board and/or respective directors, officers,
          shareholders, employees, advisors, agents, and/or bid2board’s
          third-party providers be liable for any direct, indirect, incidental
          or consequential losses or damages, including without limitation loss
          of profits and opportunity costs, business interruption, loss of data,
          trading losses, anticipated savings, loss of goodwill, reputation,
          revenue or business, whether in contract, tort (including negligence),
          breach of statutory duty, or otherwise, even if foreseeable, even if
          they have been advised of the possibility of such damages. In
          circumstances where loss or damage is suffered arising out of or in
          connection with the use, inability to use or performance of the
          Platform or any of the material contained therein or resulting
          therefrom, bid2board and/or their respective directors, officers,
          shareholders, employees, advisors, agents, and/or bid2board’s
          third-party providers shall not be liable for such loss or damage,
          whether due to inaccuracy, error, omission, delay, unavailability or
          interruption, failure in performance or any other cause whether on the
          part of bid2board and/or their respective directors, officers,
          shareholders, employees, advisors, agents, and/or bid2board’s
          third-party providers and/or their respective directors, officers,
          shareholders, employees, advisors, agents. In any event, it is your
          responsibility to evaluate the accuracy, completeness or usefulness of
          the Platform, the material contained therein and the material
          resulting therefrom. bid2board and/or their respective directors,
          officers, shareholders, employees, advisors, agents and bid2board’s
          third-party providers and/or their directors, officers, shareholders,
          employees, advisors, agents shall not be liable for any claims against
          you by third parties.
        </p>
        <p>
          The Platform or any of the material contained therein or resulting
          therefrom might contain references or links to third-party websites,
          including without limitation links to websites of third-party
          providers. bid2board assumes no responsibility for the content,
          accuracy, completeness, up-to-dateness and availability of any website
          or resources linked on the Platform or any of the material contained
          therein or resulting therefrom, or for any loss or damage that may
          arise from the use of such websites or resources. These linked
          websites and resources are provided solely for your reference and
          convenience, and not as an endorsement by bid2board of their content
          or operation. Any decision to visit a linked or referenced third-party
          website must be taken at your own risk.
        </p>
        <p>
          To the extent permitted by applicable law, the aggregate liability of
          bid2board, their respective directors, officers, shareholders,
          employees, advisors, agents and bid2board’s third-party providers in
          connection with any claim arising out of or relating to the Platform
          or any of the material contained therein or resulting therefrom, shall
          not exceed any amount paid by you, or the business or company that you
          are affiliated with, for use of the Platform in the twelve (12)
          calendar months preceding notification by you to bid2board of the
          claim. If no such amount is paid, then the aggregate liability stated
          above is equal to zero (0).
        </p>
        <p>
          The present clause shall survive any termination of the Agreement.
        </p>
        <Heading id="indemnification">
          <a href="terms-and-conditions#indemnification">Indemnification</a>
        </Heading>
        <p>
          You hereby agree and undertake to hold bid2board and their third-party
          providers, as well as their respective officers, directors, employees,
          agents, contractors, assignees, third-party providers, suppliers, and
          successors in interest (the{' '}
          <strong>&#34;Indemnified Parties&#34;</strong>) harmless against any
          and all demands, actions, claims, liabilities, damages, losses, costs,
          charges and expenses, including but not limited to attorneys’ fees and
          expenses, (the <strong>&#34;Claims&#34;</strong>) resulting or arising
          from your use of the Platform or any of the material contained therein
          or resulting therefrom or any breach of the Agreement (the
          <strong>&#34;Indemnified Causes&#34;</strong>). This undertaking shall
          apply only to the extent that the Indemnified Parties are named, or
          threatened with being so named, in an action or proceeding alleging
          that the Indemnified Causes partially or wholly gave rise to the
          alleged cause of action. The indemnification obligation shall survive
          any termination of the Agreement. bid2board will provide you with
          notice of any such Claim or allegation. bid2board always reserves the
          right to voluntarily participate in proceedings regarding such Claim
          at its own expense.
        </p>
        <Heading id="intellectual-property">
          <a href="terms-and-conditions#intellectual-property">
            Intellectual Property
          </a>
        </Heading>
        <p>
          Any technology and other similar proprietary property related to,
          forming a part of, embodied in, or necessary for use of the Platform,
          or any of the material contained therein or resulting therefrom (the
          <strong>“Intellectual Property”</strong>) are, and shall remain, the
          sole and exclusive property of bid2board, their third-party providers,
          and/or other entities that have granted bid2board and/or their
          third-party providers the right and license to use such Intellectual
          Property (the <strong>“Owners”</strong>). Owners hold and reserve all
          right, title and interest in and to their respective Intellectual
          Property, including but not limited to, copyrights, patents, trade
          secrets, database rights, trademark rights, rights to software, rights
          to computer programs, applications and other related products, rights
          to trade names, service marks, designs and other product and service
          names and logos, know-how related to the design, function or operation
          of the Platform. You acknowledge that you have no right, title or
          interest in and to the Intellectual Property. You may not use, copy,
          reproduce, retransmit, distribute, disclose, disseminate, sell,
          publish, broadcast, circulate or interfere with the Intellectual
          Property in any way without the appropriate Owner’s express prior
          written consent. Except as expressly provided herein, the Owners do
          not grant to you any express or implied rights to the Intellectual
          Property. Actual or attempted illegal or unauthorized use of the
          Intellectual Property is strictly prohibited and may result in
          criminal and/or civil prosecution. Unless otherwise provided herein,
          it is explicitly forbidden to copy or reproduce the Platform, or any
          part of its content, functionality or design, or the software and the
          technologies on which the Platform is based. For the avoidance of
          doubt, it is hereby explicitly provided that the Intellectual Property
          rights residing in the architecture and software of the Platform and
          its supporting technology comprise confidential information – in the
          sense of clause 6 of the Agreement – which is provided to you as part
          of this Agreement. Without prejudice to the interdictions contained in
          the preceding paragraph, you shall not disclose any Intellectual
          Property information and any Intellectual Property information about
          the Platform, its architecture or software, or its supporting
          technology to any third party. The present clause shall survive any
          termination of the Agreement.
        </p>
        <Heading id="termination">
          <a href="terms-and-conditions#termination">Termination</a>
        </Heading>
        <ol>
          <li>
            You are authorized to use the Platform or any of the material
            contained therein or resulting therefrom exclusively for the
            duration of the Agreement. You agree that bid2board at its sole
            discretion, may terminate the Agreement or suspend your access to
            and use of the Platform or any of the material contained therein or
            resulting therefrom, at any time, with immediate effect, if (a)
            bid2board has reasonable indication that you are accessing or using
            the Platform or any of the material contained therein or resulting
            therefrom in violation of the Agreement; or (b) you commit a breach
            of any term or condition of the Agreement and do not cure such
            breach within five (5) days of written notice thereof; or (c) you
            object to the processing of your Personal Information, as further
            provided for in the Privacy Policy; (d) your assets are transferred
            to an assignee for the benefit of creditors, to a receiver or to a
            trustee in bankruptcy, a proceeding is commenced by or against you
            for relief under bankruptcy or similar laws and such proceeding is
            not dismissed within sixty (60) days, or you are declared bankrupt;
            or (e) required by legal or regulatory authority; or (f) so
            suggested by legal counsel to protect bid2board’s liability to third
            parties; or (g) if you fail to make any payment whatsoever due and
            payable to bid2board;
          </li>
          <li>
            You may cancel your account at any time by sending an email to
            info@bid2board.com.Your registration and your right to receive the
            Service will end immediately, but you acknowledge that you will not
            receive any refund of any charges already paid in relation to the
            remainder of the relevant payment period. Furthermore, if at the
            date of termination you owe us any, we will issue an invoice to you
            for payment.
          </li>
          <li>
            Upon termination, all licenses granted herein will immediately
            terminate and all fees and expenses owed by you through the date of
            termination automatically and immediately become due and payable.
          </li>
          <li>
            Upon termination, you must immediately (i) discontinue the use of
            the Platform, the material contained therein and the material
            resulting therefrom, (ii) destroy and purge from electronic media
            any items relating to the Platform, any of the material contained
            therein and any material resulting therefrom without limitation; and
            (iii) provide a notarized written certification to bid2board that
            you have complied with this clause.
          </li>
          <li>
            Upon suspension of your access to and use of the Platform or any of
            the material contained therein or resulting therefrom, you must
            immediately discontinue the use of the Platform, the material
            contained therein and the material resulting therefrom. bid2board
            may authorize you to continue the use of the Platform or any of the
            material contained therein or resulting therefrom as soon as it is
            satisfied at its sole discretion that the reasons for which
            suspension was decided no longer apply, provided that any such
            authorization shall be given no later than fifteen (15) business
            days from the date of suspension. If no such authorization is given,
            then the Agreement shall be deemed terminated by bid2board.
          </li>
          <li>
            Accessing the Platform after such suspension or termination shall
            constitute an act of trespass. Further, you agree that bid2board
            shall not be liable to you or any third party for any suspension of
            your access to the Platform or any of the material contained therein
            or resulting therefrom, or termination of the Agreement.
          </li>
        </ol>
        <Heading id="force-majeure">
          <a href="terms-and-conditions#force-majeure">Force majeure</a>
        </Heading>
        <p>
          bid2board may be excused from the performance of any obligation under
          the Agreement due to any act or condition whatsoever beyond the
          reasonable control of, and not occasioned by the fault or negligence
          of bid2board, including, without limitation, acts of God, acts of
          terrorism, acts of nature or of a public enemy, acts of a government
          or any state or political subdivision thereof, internet outages,
          strikes, lockouts, failure of a utility service or transport or
          telecommunications network, fires, floods, explosions, wars, or other
          catastrophes, malicious damage labor disturbances, freight embargos,
          or delays of a supplier or subcontractor due to such causes.
        </p>
        <Heading id="security-measures">
          <a href="terms-and-conditions#security-measures">Security measures</a>
        </Heading>
        <p>
          bid2board may impose any security measures it reasonably deems
          appropriate to ensure compliance with the Agreement, including covert
          and overt copy-detection and license awareness technology and encoding
          of requestor/user information in printed and electronic formats. Any
          attempt to circumvent such access restrictions of bid2board security
          measures will be considered a material breach of the Agreement.
        </p>
        <Heading id="notices">
          <a href="terms-and-conditions#notices">Notices</a>
        </Heading>
        <p>Any notice given under the Agreement shall be sent via email.</p>
        <p>
          You shall communicate with bid2board at the following address:
          info@bid2board.com. bid2board shall communicate with you at the email
          address you have provided upon registration. Delivery of a notice is
          deemed to have taken place on the business day after it has been sent.
        </p>
        <Heading id="assigment">
          <a href="terms-and-conditions#assigment">Assigment</a>
        </Heading>
        <p>
          You may not assign or transfer the Agreement or any of the rights
          and/or obligations hereunder to any third party without bid2board
          prior written consent. bid2board reserves the right to assign or
          transfer the Agreement and/or any of the rights and/or obligations
          hereunder to any third person provided that it will notify you of any
          such assignment or transfer within a reasonable time after its
          execution. Any assignment in violation of this clause shall be null
          and void.
        </p>
        <Heading id="relationships-of-the-parties">
          <a href="terms-and-conditions#relationships-of-the-parties">
            Relationship of the parties
          </a>
        </Heading>
        <p>
          The parties are independent contractors. The Terms of Use do not
          create an agency, partnership, joint venture, fiduciary duty,
          employment relationship or other form of legal association between
          bid2board and you, unless otherwise explicitly described in an
          addendum to this agreement.
        </p>
        <Heading id="entire-agreement">
          <a href="terms-and-conditions#entire-agreement">Entire agreement</a>
        </Heading>
        <p>
          The Agreement constitutes the entire agreement. Nothing said by any
          person on bid2board’s behalf should be understood as a variation of
          the Agreement or as an authorized representation about the nature or
          quality of the Platform or any of the material contained therein or
          resulting therefrom.
        </p>
        <p>
          The Agreement supersedes all prior agreements, negotiations and
          discussions between you and bid2board relating to the subject matter
          of the Agreement, and you acknowledge that you have not entered into
          the Agreement in reliance on any statement or representation except in
          so far as the representation has been incorporated into the Agreement.
          Save for fraud or fraudulent misrepresentation, bid2board shall have
          no liability for any representation being untrue or misleading.
        </p>
        <Heading id="waiver">
          <a href="terms-and-conditions#waiver">Waiver</a>
        </Heading>
        <p>
          A failure or delay by bid2board to exercise any right or remedy
          provided under this Agreement or by law shall not constitute a waiver
          of that or any other right or remedy, nor shall it prevent or restrict
          any further exercise of that or any other right or remedy. No single
          or partial exercise of any right or remedy provided under this
          Agreement or by law shall prevent or restrict the further exercise of
          that or any other right or remedy.
        </p>
        <Heading id="governing-law-juresdiction">
          <a href="terms-and-conditions#governing-law-juresdiction">
            Governing law/ Jurisdiction
          </a>
        </Heading>
        <p>
          The Agreement and its performance (including any non-contractual
          obligations) shall be governed by Greek Law. The parties shall submit
          to the exclusive jurisdiction of the courts of Piraeus for any dispute
          or claim arising out of or in connection with the Agreement.
        </p>
        <Title>Website Terms of Use</Title>
        <p>
          This website <strong>https://bid2board.com</strong> (“Website”) is a
          computer online service, provided by bid2board PC, a private limited
          company organized and existing under the laws of Greece, whose
          registered office is at 2 Afentouli Street, Piraeus, 18536, Greece.
        </p>
        <ol>
          <Heading id="acceptance-of-the-terms-of-use">
            <a href="terms-and-conditions#acceptance-of-the-terms-of-use">
              <li>Acceptance of the Terms of Use</li>
            </a>
          </Heading>

          <p>
            The Website is accessible to the user through any electronic devices
            (e.g. personal computer, tablet, mobile phone). By using the
            Website, the user acknowledges that he/she has read and understood
            the present Terms of Use (“Terms of Use”), that he/she agrees to be
            bound by all of its provisions and that he/she hereby irrevocably
            waives any complaint, claim or demand whatsoever against bid2board
            with respect to any liability arising from or connected in any way
            with the use of this Website.
          </p>
          <p>
            bid2board may amend or modify the Terms of Use at any time at its
            sole discretion and without prior notice. Any use of the Website by
            the user after such amendment has been uploaded to the Website,
            shall be deemed to constitute acceptance by the user of such
            amendments or modifications.
          </p>

          <Heading id="users-obligations">
            <a href="terms-and-conditions#users-obligations">
              <li>User’s obligations</li>
            </a>
          </Heading>

          <p>
            The user agrees to use the Website, in accordance with the Terms of
            Use, only for his/her personal information and only for lawful
            purposes.
          </p>
          <p>
            It is expressly prohibited to copy, take screenshots, distribute,
            publish, transfer, store, permanently retain, disseminate,
            distribute, transmit, scan, publish on a network, display, or
            otherwise reproduce, disclose, modify, download, translate into any
            language or make available to others, commercially exploit, or
            create derivative works from the Website or any of the material
            contained therein.
          </p>
          <p>
            The user acknowledges that the Website and the material contained
            therein, regardless of the form or the medium on which they are
            delivered or displayed, are developed entirely at private expense,
            comprise valued proprietary and commercial information of bid2board
            and are the property of bid2board.
          </p>
          <p>
            The user acknowledges that the Website may contain information,
            software, graphics or other material that are protected by
            copyright, trademarks, database rights, patents, trade secrets,
            rights to software, rights to computer programs, applications and
            other related products, rights to trade names, service marks,
            designs and other product and service names and logos, know-how
            related to the design, function or operation of the Website, or
            other proprietary rights (“Intellectual Property”). bid2board holds
            and reserves all right, title and interest in and to the respective
            Intellectual Property.
          </p>
          <p>
            The user acknowledges that he/she has no right, title or interest in
            and to the Intellectual Property. The user may not use, copy,
            reproduce, retransmit, distribute, disseminate, sell, publish,
            broadcast, circulate or interfere with the Intellectual Property in
            any way without bid2board’s express prior written consent.
          </p>
          <p>
            The user should not engage in any conduct that in any way restricts
            or inhibits any other person from using the Website. The user should
            avoid posting inaccurate information. The user is prohibited from
            posting on or transmitting through the Website any unlawful,
            harmful, threatening, abusive, harassing, defamatory, obscene,
            vulgar, hateful, racially, ethnically or otherwise objectionable
            material of any kind, including but not limited to, any material
            which encourages conduct that would constitute a criminal offense,
            give rise to civil liability or otherwise violate any local, state,
            national or international law in force.
          </p>
          <p>
            The user should refrain from using software viruses, or any other
            computer code, files or programs that may interrupt, destroy or
            limit the functionality of any computer software or hardware or
            telecommunications equipment of bid2board or of any other user of
            the Website.
          </p>
          <p>
            The user is responsible for all charges (e.g. telephone, internet
            service, etc.) associated with the connection to the Website.
          </p>
          <Heading id="bid2board-rights">
            <a href="terms-and-conditions#bid2board-rights">
              <li>bid2board’s rights</li>
            </a>
          </Heading>

          <p>
            bid2board reserves the right, at its sole discretion and without
            prior notice, to restrict, suspend or alter any part – or the
            entirety – of the Website, including but not limited to (i) restrict
            the time of availability and (ii) restrict or increase the
            availability and/or purpose of the Website (e.g. computer types and
            operating systems).
          </p>
          <Heading id="privacy-policy">
            <a href="terms-and-conditions#privacy-policy">
              <li>Privacy Policy</li>
            </a>
          </Heading>
          <p>
            The user acknowledges that he/she has carefully read the{' '}
            <strong>Privacy Policy</strong> before deciding to use the Website,
            which governs bid2board’s treatment of any information, including
            personally identifiable information, submitted by the user.
          </p>
          <Heading id="disclaimer-website-terms">
            <a href="terms-and-conditions#disclaimer-website-terms">
              <li>Disclaimer</li>
            </a>
          </Heading>
          <p>
            The information contained in this Website is provided on an “as is”
            and “as available” “without prejudice” basis. It is not
            comprehensive and is subject at any time to revision and/or change
            and/or cancellation and/or modification and/or alteration without
            any notice whatsoever. The user is using the Website entirely at
            his/her own risk under these Terms of Use. If the user does not
            agree with any of these terms of use, he/she shall refrain from
            using this Website.
          </p>
          <p>
            bid2board gives no warranties of any kind concerning the Website or
            any of the material contained therein. bid2board expressly disclaims
            all warranties, whether express or implied, of availability,
            accuracy, completeness, currency, reliability, non-infringement of
            third parties’ rights, merchantability or fitness for a particular
            purpose of any material contained in, displayed on, resulting from
            or otherwise available from the Website. bid2board will in no way be
            responsible or liable for any damages, direct, indirect, special,
            punitive or consequential of any kind whatsoever arising out of or
            in connection with the use, contents or information of this Website,
            which is intended for general guidance only, and which is, as
            aforesaid, subject to changes without prior notice. bid2board, its
            principals, directors, officers and employees make no
            representations or warranties of any kind whatsoever in this
            Website, either express or implied. bid2board also disclaims all
            responsibility whatsoever for any damages caused by viruses
            contained in this Website or within any electronic files sent from
            bid2board or from this Website.
          </p>
          <p>
            Neither bid2board, nor any of its external partners, agents,
            affiliates and/or other related parties guarantee that the Website
            will function without interruption or functional errors. In
            particular, the operation of the Website may be interrupted,
            indicatively due to maintenance, updates, or system or network
            failures. bid2board disclaims all liability for any damages caused
            by any such interruption or errors in functioning. Furthermore,
            bid2board disclaims all liability for any malfunctioning,
            impossibility of access or poor use conditions of the Website due to
            inappropriate equipment, disturbances related to internet service
            providers, to the saturation of the internet network, and for any
            other reason.
          </p>
          <p>
            The Website may contain references or links to third-party websites.
            bid2board assumes no responsibility for the content, accuracy,
            completeness, up-to-dateness and availability of any website or
            resources linked on the Website or any of the material contained
            therein, or for any loss or damage that may arise from the use of
            such websites or resources. These linked websites and resources are
            provided solely for your reference and convenience, and not as an
            endorsement by bid2board of their content or operation. Any decision
            to visit a linked or referenced third-party website must be taken at
            your own risk.
          </p>
          <Heading id="governing-law-juresdiction-website-terms">
            <a href="terms-and-conditions#governing-law-juresdiction-website-terms">
              <li>Governing law/ Jurisdiction</li>
            </a>
          </Heading>
          <p>
            The Agreement and its performance (including any non-contractual
            obligations) shall be governed by Greek Law. The parties shall
            submit to the exclusive jurisdiction of the courts of Piraeus for
            any dispute or claim arising out of or in connection with the
            Agreement.
          </p>
        </ol>
      </Wrapper>
    </Layout>
  );
};

export default TermsAndConditions;
